import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import {
  Badge,
  BlockStack,
  Button,
  Card,
  InlineStack,
  Text,
} from "@shopify/polaris";

import type { ConciergeSettings, ShopMe } from "../services/brilio";

/**
 * What still stands between a fresh install and an assistant a shopper can see.
 *
 * Two Built for Shopify criteria failed on the same fact. 4.2.2 (helpful
 * onboarding): there was no onboarding anywhere in the app. 4.2.3 (helpful
 * homepage): this app ships a theme app extension that renders NOTHING until
 * the merchant switches the app embed on in their theme editor, and the
 * Dashboard badged the assistant "Live" regardless — that badge is computed
 * from install_status + is_active, neither of which knows anything about the
 * merchant's theme.
 *
 * Every step resolves from evidence the page already holds or from App Bridge.
 * Where the evidence does not exist the step SAYS SO rather than guessing.
 *
 * Straight ASCII apostrophes throughout. The copy here is passed as props, not
 * written as JSX text, so the `&rsquo;` entities used elsewhere in this app
 * would render literally — and Onboarding.test.tsx matches these strings.
 *
 * Deliberately no <ProgressBar>: DashboardTruthfulness.test.tsx asserts the
 * Dashboard renders none, because the one it used to have measured a quota that
 * enforces nothing. "N of 3 done" is text.
 */

/**
 * Whether the app embed is on in the merchant's PUBLISHED theme.
 *
 * "unknown" is neither a loading state nor a polite "off" — it is the answer
 * when App Bridge does not tell us, and it has to stay distinct from both.
 * Ticking this step while the embed is off sends a merchant away believing
 * shoppers can see a chat that is not on their storefront; saying "we can't
 * tell" costs them one look at the theme editor.
 */
export type EmbedStatus = "checking" | "on" | "off" | "unknown";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * Reduce App Bridge's extension list to the one question this card asks.
 *
 * Read structurally, over `unknown`, and NOT against App Bridge's own
 * `ExtensionInfo`: that type's `activations` is a conditional keyed on its type
 * parameter (@shopify/app-bridge-types dist/shopify.ts:406), so with the
 * default parameter it distributes to
 * `UiExtensionActivation[] | ThemeExtensionActivation[]` and
 * `ext.type === "theme_app_extension"` does not narrow it — every read of
 * `.status` would be a compile error. This is host-provided data either way.
 *
 * Matched on `type`, never on `handle`: the handle has been reported coming
 * back stale and generic from this API. This app ships exactly one theme app
 * extension (concierge-chat) with one block (blocks/chat.liquid, target
 * "body"), so "any active theme-app-extension block" is unambiguous here.
 *
 * Nothing readable means UNKNOWN, not off. An empty list is a shape this API
 * has actually returned in the wild, and "App Bridge told us nothing" must
 * never be rendered to a merchant as "you haven't done it".
 */
export function readEmbedStatus(extensions: readonly unknown[]): EmbedStatus {
  const blockStatuses: string[] = [];
  for (const extension of extensions) {
    if (!isRecord(extension)) continue;
    if (extension.type !== "theme_app_extension") continue;
    const activations = extension.activations;
    if (!Array.isArray(activations)) continue;
    for (const activation of activations) {
      if (isRecord(activation) && typeof activation.status === "string") {
        blockStatuses.push(activation.status);
      }
    }
  }
  if (blockStatuses.length === 0) return "unknown";
  // "active" is App Bridge's word for present in the published theme AND
  // enabled. "available" is the block existing but switched off; "unavailable"
  // is it disabled by an availability condition. Neither of those two puts
  // anything in front of a shopper, so neither is "on".
  return blockStatuses.includes("active") ? "on" : "off";
}

function useEmbedStatus(): {
  status: EmbedStatus;
  recheck: () => void;
  rechecked: boolean;
} {
  const [status, setStatus] = useState<EmbedStatus>("checking");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    const app = window.shopify?.app;
    // Loaded outside the admin iframe there is no `shopify` global at all, and
    // an App Bridge bundle predating the App API has no `app` member. Both are
    // "we cannot tell", never "it is off".
    if (typeof app?.extensions !== "function") {
      setStatus("unknown");
      return;
    }
    setStatus("checking");
    app.extensions().then(
      (extensions) => {
        if (alive) setStatus(readEmbedStatus(extensions));
      },
      // Documented to reject when the App API is unavailable or the host call
      // fails. Same answer as an unreadable list: we do not know.
      () => {
        if (alive) setStatus("unknown");
      },
    );
    return () => {
      alive = false;
    };
  }, [attempt]);

  return {
    status,
    recheck: useCallback(() => setAttempt((n) => n + 1), []),
    rechecked: attempt > 0,
  };
}

/**
 * The assistant name the BACKEND generates for a shop nobody has renamed:
 * `acme-store.myshopify.com` -> `Acme Store Assistant`. A mirror of
 * `ShopifyAppController._concierge_agent_name` (api/controllers/shopify_app.py).
 *
 * The mirror exists because there is no "the merchant edited this" flag on the
 * wire. `_build_concierge_agent` seeds agent_name, greeting AND persona at
 * activation, so every active shop has all three filled in — a non-empty test
 * here would tick this step for a merchant who has never opened Settings, which
 * is the one thing a checklist must never do.
 *
 * The suffix is the fragile half, and the Concierge -> Assistant rename is
 * exactly the shape of change that breaks it: rename the backend's suffix
 * without this line and every generated name stops matching, so this step
 * reports that EVERY merchant has renamed their assistant. Onboarding.test.tsx
 * pins both halves.
 */
export function generatedAgentName(shopDomain: string): string {
  return `${prettyStoreName(shopDomain)} Assistant`.slice(0, 255);
}

/** The store half of a generated name: `acme-store.myshopify.com` -> `Acme Store`. */
function prettyStoreName(shopDomain: string): string {
  const handle = shopDomain.split(".")[0].replace(/[-_]/g, " ");
  // Python's str.title(): each run of letters gets its first character upper
  // and the rest lower, and every non-letter is a word boundary ("shop2go" ->
  // "Shop2Go"). ASCII-only is faithful enough — a myshopify handle is
  // [a-z0-9-] and cannot contain a letter this class would miss.
  const pretty = handle
    .replace(
      /[A-Za-z]+/g,
      (word) => word[0].toUpperCase() + word.slice(1).toLowerCase(),
    )
    .trim();
  return pretty || "Store";
}

/**
 * Every suffix the backend has ever generated, newest first. `Concierge` is
 * here because the rename does NOT touch the database: a shop installed before
 * it still holds `Acme Store Concierge`, and that name is just as much "we
 * generated this, nobody has edited it" as today's. Matching only the current
 * suffix would tick this step for every pre-rename install — the exact failure
 * the comment above warns about, arriving from the other direction.
 *
 * Never delete an entry. A suffix leaves this list only when the rows that
 * carry it are gone from the database.
 */
const GENERATED_AGENT_NAME_SUFFIXES = ["Assistant", "Concierge"] as const;

/** Is this name one the backend generated, rather than one a merchant chose? */
export function isGeneratedAgentName(
  name: string,
  shopDomain: string,
): boolean {
  const pretty = prettyStoreName(shopDomain);
  return GENERATED_AGENT_NAME_SUFFIXES.some(
    (suffix) => name === `${pretty} ${suffix}`.slice(0, 255),
  );
}

/**
 * The greeting `_build_concierge_agent` seeds — CONCIERGE_GREETING in
 * api/controllers/shopify_app.py, copied verbatim.
 *
 * The copy is the fragile half and it fails in the DANGEROUS direction: reword
 * that constant on the backend without touching this line and every shop
 * provisioned afterwards has a greeting that differs from this one, so this
 * step starts reporting "done" for merchants who have never opened Settings.
 * Onboarding.test.tsx reads the Python source and fails when the two drift —
 * that test is the reason this duplication is allowed to exist.
 */
export const SEEDED_GREETING =
  "Hi! \u{1F44B} I'm your shopping assistant. Ask me about products, sizing, shipping, or your order — I'm here to help you find what you need.";

/** Has the merchant replaced either of the two things a shopper is greeted by? */
export function hasOwnConciergeVoice(
  concierge: ConciergeSettings,
  shopDomain: string,
): boolean {
  const name = (concierge.agent_name ?? "").trim();
  const greeting = (concierge.greeting ?? "").trim();
  // A shop with no agent row has BOTH null. Nothing has been made anyone's own,
  // so an absent value is "not done" — it is never evidence of an edit.
  const ownName = name !== "" && !isGeneratedAgentName(name, shopDomain);
  const ownGreeting = greeting !== "" && greeting !== SEEDED_GREETING;
  return ownName || ownGreeting;
}

type StepState = "done" | "todo" | "unknown" | "checking";

function StepBadge({ state }: { state: StepState }) {
  if (state === "done") return <Badge tone="success">Done</Badge>;
  // Words, not a tick or a cross, precisely because this state is neither.
  if (state === "unknown") return <Badge tone="warning">Can't tell</Badge>;
  if (state === "checking") return <Badge>Checking</Badge>;
  return <Badge>To do</Badge>;
}

function Step({
  state,
  title,
  detail,
  actions,
}: {
  state: StepState;
  title: string;
  detail: string;
  actions?: ReactNode;
}) {
  return (
    <BlockStack gap="150">
      <InlineStack gap="200" blockAlign="center">
        <StepBadge state={state} />
        <Text as="h4" variant="headingSm">
          {title}
        </Text>
      </InlineStack>
      <Text as="p" tone="subdued" variant="bodySm">
        {detail}
      </Text>
      {actions ? (
        <InlineStack align="start" gap="200" blockAlign="center">
          {actions}
        </InlineStack>
      ) : null}
    </BlockStack>
  );
}

export function OnboardingChecklist({ shop }: { shop: ShopMe }) {
  const { status: embed, recheck, rechecked } = useEmbedStatus();

  const policies = shop.policies_indexed ?? 0;
  const policiesDone = policies > 0;
  const voiceDone = hasOwnConciergeVoice(shop.concierge, shop.shop_domain);
  const embedDone = embed === "on";
  const doneCount = [embedDone, policiesDone, voiceDone].filter(Boolean).length;

  // Opens the theme editor on its App embeds panel. Deliberately NOT an
  // `activateAppId` deep link: that needs the extension's Shopify-side UUID,
  // and the id in shopify.extension.toml is a CLI-local one — a deep link that
  // silently lands on the wrong panel is worse than a reliable generic one.
  // `external` rather than target="_blank": Polaris pairs the new tab with
  // rel="noopener noreferrer" itself. A new tab is required either way — the
  // admin renders this app inside an iframe.
  const themeEditorUrl = `https://${shop.shop_domain}/admin/themes/current/editor?context=apps`;

  // A plain link, not `useNavigate`: Dashboard is rendered without a Router by
  // three test files, and a router hook would throw in all of them. So this is
  // a full document load, and `host` has to be carried by hand or the SPA
  // reloads outside its App Bridge context.
  //
  // Copied selectively, NOT `window.location.search` wholesale: a real embedded
  // load carries `id_token`, `hmac` and `session` too, and those have no
  // business sitting in an href a merchant can copy out of the page.
  const settingsUrl = settingsHref(window.location.search);

  // "unknown" is terminal for a merchant whose App Bridge bundle has no App
  // API, so it must not keep the card open forever — a homepage that nags a
  // merchant who has finished is the criterion this card exists to pass,
  // failed a different way. It collapses on an honest line instead of a tick.
  const embedSettled = embed === "on" || embed === "unknown";

  if (embedSettled && policiesDone && voiceDone) {
    return (
      <Card>
        <InlineStack
          gap="300"
          blockAlign="center"
          align="space-between"
          wrap={false}
        >
          <InlineStack gap="200" blockAlign="center">
            {shop.concierge.is_active ? (
              <Badge tone={embedDone ? "success" : "warning"}>
                {embedDone ? "Done" : "Almost"}
              </Badge>
            ) : (
              <Badge tone="attention">Paused</Badge>
            )}
            <Text as="p">
              {/* Three different facts, so three different sentences. Claiming
                  "the app embed is on" on the unknown path, or "setup complete"
                  for an assistant the merchant has switched off, is the same lie
                  this card was built to stop — told from the collapsed state
                  where nobody would look for it. */}
              {!shop.concierge.is_active
                ? "Setup is finished, but your assistant is switched off — shoppers won't see the chat until you switch it back on in Settings."
                : embedDone
                  ? "Setup complete — the app embed is on, your policies are in, and you've made the assistant's name or greeting your own."
                  : "Your policies are in and the assistant's name or greeting is your own. We can't confirm the app embed from here — check that AI sales chat, listed under Brilio Assistant, is switched on in App embeds."}
            </Text>
          </InlineStack>
          <InlineStack gap="200" blockAlign="center" wrap={false}>
            {/* A paused assistant is fixed in Settings, not in the theme
                editor — sending the merchant to the embeds panel would have
                them turn on a switch that is already on. */}
            {shop.concierge.is_active ? null : (
              <Button url={settingsUrl}>Turn it back on</Button>
            )}
            {/* Kept after the collapse: the theme editor is where the merchant
                changes the widget's position and colour, and this card is the
                only route to it from this app. */}
            <Button url={themeEditorUrl} external variant="plain">
              Open theme editor
            </Button>
          </InlineStack>
        </InlineStack>
      </Card>
    );
  }

  // Named for what the THEME EDITOR draws, which is not what this app is
  // called. Each row in App embeds is the BLOCK's name ("AI sales chat") with
  // the app title ("Brilio Assistant") beneath it — Shopify renders the title
  // itself, which is why the block name deliberately does not repeat it. These
  // lines used to say "Brilio Concierge", a string that appeared nowhere in
  // that list, and a merchant hunting a row that does not exist concludes the
  // install is broken. Both names now, in the order they are stacked on screen.
  const embedDetail: Record<EmbedStatus, string> = {
    checking:
      "Checking whether the chat is switched on in your published theme.",
    on: "The AI sales chat app embed is on in your published theme.",
    off: "Shoppers can't see the chat yet. In your theme editor, open App embeds and turn on AI sales chat, listed under Brilio Assistant.",
    unknown:
      "We can't tell whether the app embed is on — Shopify didn't report a status for it. Open your theme editor, go to App embeds, and look for AI sales chat, listed under Brilio Assistant.",
  };

  const embedState: StepState =
    embed === "on"
      ? "done"
      : embed === "checking"
        ? "checking"
        : embed === "unknown"
          ? "unknown"
          : "todo";

  return (
    <Card>
      <BlockStack gap="400">
        <BlockStack gap="100">
          <Text as="h3" variant="headingSm">
            Finish setting up your assistant
          </Text>
          <Text as="p" tone="subdued" variant="bodySm">
            {`${doneCount} of 3 done.`}
          </Text>
        </BlockStack>

        <Step
          state={embedState}
          title="Turn on the app embed in your theme"
          detail={embedDetail[embed]}
          actions={
            embedDone ? null : (
              <>
                <Button url={themeEditorUrl} external>
                  Open theme editor
                </Button>
                {embed === "checking" ? null : (
                  <Button variant="plain" onClick={recheck}>
                    Check again
                  </Button>
                )}
                {/* The theme editor opens in another tab, so the merchant comes
                    back to a stale answer and presses this. If the answer does
                    not move, say what to do next rather than letting the button
                    teach them the app is broken — nothing here establishes that
                    App Bridge re-queries the host per call rather than handing
                    back a snapshot taken when the app loaded. */}
                {rechecked && embed !== "checking" ? (
                  <Text as="span" tone="subdued" variant="bodySm">
                    Switched it on and still seeing this? Reload this page.
                  </Text>
                ) : null}
              </>
            )
          }
        />

        <Step
          state={policiesDone ? "done" : "todo"}
          title="Read my policies"
          detail={
            policiesDone
              ? `Your assistant can answer from ${policies} store ${
                  policies === 1 ? "policy" : "policies"
                }.`
              : "Your assistant can't answer returns or shipping questions until it has read your store policies."
          }
          actions={
            policiesDone ? null : (
              <Button url={settingsUrl}>Read them in Settings</Button>
            )
          }
        />

        <Step
          state={voiceDone ? "done" : "todo"}
          title="Give the assistant a name and greeting"
          detail={
            voiceDone
              ? "You've replaced the name or greeting we started you off with."
              : "Shoppers see the name and greeting we picked for you. Make them yours so the chat sounds like your store."
          }
          actions={
            voiceDone ? null : (
              <Button url={settingsUrl}>Edit in Settings</Button>
            )
          }
        />
      </BlockStack>
    </Card>
  );
}

/**
 * `/settings` with only the params the embedded app needs to boot again.
 *
 * `host` is the one App Bridge cannot work without; `shop` and `embedded` are
 * the two the admin sends alongside it. Everything else the admin appends is
 * either single-use or a credential.
 */
function settingsHref(search: string): string {
  const carried = new URLSearchParams();
  const current = new URLSearchParams(search);
  for (const key of ["host", "shop", "embedded"]) {
    const value = current.get(key);
    // `!== null`, not truthiness: `embedded=` with an empty value is still the
    // admin telling us something, and dropping it changes how the app boots.
    if (value !== null) carried.set(key, value);
  }
  const query = carried.toString();
  return query ? `/settings?${query}` : "/settings";
}
